import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  User, Mail, Phone, Calendar, Shield, Key,
  Edit, Save, X, CheckCircle, AlertCircle, RefreshCw,
  Lock, Camera
} from 'lucide-react';
import toast from 'react-hot-toast';
import profileService from '../services/profileService';
import { useAuth } from '../contexts/AuthContext';
import { FormLoader } from '../components/CommonLoader';
import { validateEmail, validatePhone, validateName, ErrorMessage } from '../utils/validation';

const Profile = () => {
  const { user: authUser, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_no: ''
  });
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: '',
    otp: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await profileService.getProfile();
      setProfile(response.data || response);
      setFormData({
        first_name: response.data?.first_name || response.first_name || '',
        last_name: response.data?.last_name || response.last_name || '',
        email: response.data?.email || response.email || '',
        phone_no: response.data?.phone_no || response.phone_no || ''
      });
    } catch (error) {
      toast.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const validateProfileForm = () => {
    const errors = {};
    
    // First Name validation
    const firstNameError = validateName(formData.first_name, 'First name');
    if (firstNameError) errors.first_name = firstNameError;
    
    // Last Name validation
    const lastNameError = validateName(formData.last_name, 'Last name');
    if (lastNameError) errors.last_name = lastNameError;
    
    // Email validation
    const emailError = validateEmail(formData.email);
    if (emailError) errors.email = emailError;
    
    // Phone validation
    const phoneError = validatePhone(formData.phone_no);
    if (phoneError) errors.phone_no = phoneError;
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateProfile = async () => {
    if (!validateProfileForm()) {
      toast.error('Please fix the validation errors');
      return;
    }
    
    try {
      const response = await profileService.updateProfile(formData);
      setProfile(response.data || response);
      setEditing(false);
      setFormErrors({});
      
      // Update auth context if needed
      if (updateUser) {
        updateUser(response.data || response);
      }
      
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleRequestOTP = async () => {
    try {
      await profileService.requestPasswordUpdateOTP();
      setOtpSent(true);
      toast.success('OTP sent to your registered email/phone');
    } catch (error) {
      toast.error('Failed to send OTP');
    }
  };

  const handleUpdatePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (!passwordData.otp) {
      toast.error('Please enter OTP');
      return;
    }

    try {
      await profileService.updatePassword({
        new_password: passwordData.new_password,
        otp: passwordData.otp
      });
      toast.success('Password updated successfully');
      setShowPasswordModal(false);
      setPasswordData({ new_password: '', confirm_password: '', otp: '' });
      setOtpSent(false);
    } catch (error) {
      toast.error('Failed to update password');
    }
  };

  const cancelEdit = () => {
    setFormData({
      first_name: profile?.first_name || '',
      last_name: profile?.last_name || '',
      email: profile?.email || '',
      phone_no: profile?.phone_no || ''
    });
    setEditing(false);
    setFormErrors({});
  };

  const getInitials = () => {
    const firstName = profile?.first_name || authUser?.first_name || '';
    const lastName = profile?.last_name || authUser?.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getRoleBadge = () => {
    const roles = profile?.roles || authUser?.roles || [];
    const primaryRole = Array.isArray(roles) ? roles[0] : roles;
    
    if (!primaryRole) return null;
    
    const roleColors = {
      'super-admin': 'purple',
      'admin': 'blue',
      'manager': 'green',
      'user': 'gray'
    };
    
    const color = roleColors[primaryRole.name || primaryRole] || 'gray';
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${color}-100 text-${color}-800`}>
        <Shield className="w-4 h-4 mr-2" />
        {primaryRole.display_name || primaryRole.name || primaryRole}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <FormLoader />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Page Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
          <p className="text-sm text-gray-600 mt-1">Manage your personal information and security settings</p>
        </div>
        <button
          onClick={fetchProfile}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Profile Content */}
      <>
          {/* Profile Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Profile Header */}
            <div className="bg-primary-600 p-6">
          <div className="flex items-center space-x-6">
            <div className="relative">
              <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center text-primary-600 text-2xl font-bold shadow-lg">
                {getInitials()}
              </div>
              <button className="absolute bottom-0 right-0 p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors">
                <Camera className="w-4 h-4 text-gray-600" />
              </button>
            </div>
            <div className="flex-1 text-white">
              <h2 className="text-2xl font-bold">
                {profile?.first_name} {profile?.last_name}
              </h2>
              <p className="text-primary-100 mt-1">ID: #{profile?.id || authUser?.id}</p>
              <div className="mt-3">
                {getRoleBadge()}
              </div>
            </div>
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="px-3 py-1.5 text-sm bg-white text-primary-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* Profile Details */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name {editing && <span className="text-red-500">*</span>}
              </label>
              {editing ? (
                <>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => {
                      setFormData({ ...formData, first_name: e.target.value });
                      if (formErrors.first_name) {
                        setFormErrors({ ...formErrors, first_name: '' });
                      }
                    }}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                      formErrors.first_name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                    }`}
                    required
                  />
                  {formErrors.first_name && <ErrorMessage error={formErrors.first_name} />}
                </>
              ) : (
                <p className="text-gray-900">{profile?.first_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name {editing && <span className="text-red-500">*</span>}
              </label>
              {editing ? (
                <>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => {
                      setFormData({ ...formData, last_name: e.target.value });
                      if (formErrors.last_name) {
                        setFormErrors({ ...formErrors, last_name: '' });
                      }
                    }}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                      formErrors.last_name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                    }`}
                    required
                  />
                  {formErrors.last_name && <ErrorMessage error={formErrors.last_name} />}
                </>
              ) : (
                <p className="text-gray-900">{profile?.last_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email Address {editing && <span className="text-red-500">*</span>}
              </label>
              {editing ? (
                <>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value.toLowerCase() });
                      if (formErrors.email) {
                        setFormErrors({ ...formErrors, email: '' });
                      }
                    }}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                      formErrors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                    }`}
                    required
                  />
                  {formErrors.email && <ErrorMessage error={formErrors.email} />}
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-gray-900">{profile?.email}</p>
                  {profile?.email_verified_at && (
                    <CheckCircle className="w-4 h-4 text-green-500" title="Verified" />
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                Phone Number {editing && <span className="text-red-500">*</span>}
              </label>
              {editing ? (
                <>
                  <input
                    type="tel"
                    value={formData.phone_no}
                    onChange={(e) => {
                      setFormData({ ...formData, phone_no: e.target.value });
                      if (formErrors.phone_no) {
                        setFormErrors({ ...formErrors, phone_no: '' });
                      }
                    }}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                      formErrors.phone_no ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                    }`}
                    required
                  />
                  {formErrors.phone_no && <ErrorMessage error={formErrors.phone_no} />}
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-gray-900">{profile?.phone_no}</p>
                  {profile?.phone_verified_at && (
                    <CheckCircle className="w-4 h-4 text-green-500" title="Verified" />
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Member Since
              </label>
              <p className="text-gray-900">
                {new Date(profile?.created_at || authUser?.created_at).toLocaleDateString()}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Shield className="w-4 h-4 inline mr-1" />
                Account Status
              </label>
              <div className="flex items-center gap-2">
                {profile?.status === 'active' ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-green-700">Active</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <span className="text-red-700">Inactive</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {editing && (
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={cancelEdit}
                className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleUpdateProfile}
                className="bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 px-3 py-1.5 text-sm"
              >
                <Save className="w-4 h-4" />
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Security Section */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Settings</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Password</h4>
              <p className="text-sm text-gray-500">Update your password regularly to keep your account secure</p>
            </div>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm"
            >
              <Key className="w-4 h-4" />
              Change Password
            </button>
          </div>
        </div>
      </div>
      </>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full"
          >
            <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
            
            {!otpSent ? (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  We'll send an OTP to your registered email/phone to verify your identity.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowPasswordModal(false)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRequestOTP}
                    className="bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors px-3 py-1.5 text-sm"
                  >
                    Send OTP
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-primary-500 text-sm"
                    placeholder="Enter new password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-primary-500 text-sm"
                    placeholder="Confirm new password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OTP Code
                  </label>
                  <input
                    type="text"
                    value={passwordData.otp}
                    onChange={(e) => setPasswordData({ ...passwordData, otp: e.target.value })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-primary-500 text-sm"
                    placeholder="Enter OTP"
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowPasswordModal(false);
                      setOtpSent(false);
                      setPasswordData({ new_password: '', confirm_password: '', otp: '' });
                    }}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdatePassword}
                    className="bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors px-3 py-1.5 text-sm"
                  >
                    Update Password
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Profile;